import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { chartTooltipProps } from './chartTooltip';

interface SpendingTrendsChartProps {
  data: Array<{ period: string; amount: number; category?: string }>;
  height?: number;
}

/**
 * Formats a numeric value as a compact USD currency string.
 *
 * Args:
 *   value (number): The monetary amount to format.
 *
 * Returns:
 *   string: Formatted currency string (e.g. "$1,234.56").
 */
const formatCurrency = (value: number): string =>
  `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A line chart showing spending trends over time with a gradient fill.
 *
 * Args:
 *   data (Array): Array of objects with period, amount, and optional category.
 *   height (number): Chart height in pixels (default 350).
 *
 * Returns:
 *   React.ReactElement: The rendered spending trends chart.
 */
export const SpendingTrendsChart: React.FC<SpendingTrendsChartProps> = ({
  data,
  height = 350,
}) => {
  // Reason: SVG attributes don't resolve CSS var(), so all chart colours come
  // from the resolved MUI palette — the previous hardcoded navy/greys were
  // invisible against the dark theme.
  const theme = useTheme();
  const lineColor = theme.palette.secondary.main;

  if (data.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
        No spending trend data available
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="spendingGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={lineColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={lineColor} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="period"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            {...chartTooltipProps}
            formatter={(value: number) => [formatCurrency(value), 'Spending']}
          />
          <Line
            type="monotone"
            dataKey="amount"
            stroke={lineColor}
            strokeWidth={2.5}
            isAnimationActive={false}
            dot={{ r: 4, fill: lineColor, strokeWidth: 2, stroke: theme.palette.background.default }}
            activeDot={{ r: 6, fill: lineColor, strokeWidth: 2, stroke: theme.palette.background.default }}
            fill="url(#spendingGradient)"
            fillOpacity={1}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
